import { t } from "i18next"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { ACTION_TYPES, CONTENT_TYPES, EVENT_NAMES } from "utils/gtm/gtmEventsData"

import { Radio, Row, Tooltip } from "antd"
import { DateRangePicker, Group, SelectInput, Text, Title } from "components/common"
import MonthPicker from "components/common/monthPicker/monthPicker"
import { Filters } from "components/custom"
import { TIME_DATE_FORMAT } from "constants"
import { reportTypes, staffTypes } from "features/inventory/data/constants"
import { useLazyGetRegionsQuery } from "features/shared/api"
import { SavedSearches } from "features/shared/components"
import dayjs from "dayjs"
import { useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"
import { getStoreCacheData, mapQueryStringToFilterObject } from "utils"
import { dateTimeFunction } from "utils/date"
import { fireEvent } from "utils/gtm/gtmEventHandler"
import getPrivilege from "utils/privileges/privileges"
import styles from "./index.module.less"

export default function ReportsFilters(props) {
  const {
    queryObj,
    apiKey,
    searchModule,
    useQuery,
    onFilterClear,
    privilegeKey,
    from,
    isFetching,
    isMonthPicker = false,
    canSelectCurrentDate = false,
    withStaffTypeFilter = false,
    withReportTypeFilter = false,
    ignoreRegionFilter = false,
    excludeCurrentDate = false,
    title = "",
  } = props
  const savedSearchesRef = useRef()
  const filtersRef = useRef()
  const [searchParams] = useSearchParams()
  const userPrivileges = useSelector(state => state.app.privileges)
  const user = useSelector(state => state.app?.user?.info)
  const [getRegions, { data: regionsData }] = useLazyGetRegionsQuery()
  const [getStaff, { data: staffData, isFetching: staffLoading }] = useQuery()

  const lastSyncedDate = useMemo(() => {
    if (!isFetching) {
      let cachedData = getStoreCacheData(apiKey, queryObj)
      return cachedData?.latestSyncDate
    }
  }, [isFetching])

  useEffect(() => {
    let query = { ...queryObj }
    delete query?.load
    getStaff({ regionId: queryObj?.regionId, userId: user?.id })
  }, [])

  useEffect(() => {
    getPrivilege(privilegeKey, userPrivileges, "all") && !ignoreRegionFilter && getRegions()
  }, [userPrivileges, ignoreRegionFilter])

  const onFilterValueChange = (filterItem, item, value) => {
    const content_type = filterItem.content_type
    if (value === "regionId") {
      getStaff(item ? { [value]: item.value, userId: user?.id } : { userId: user?.id })
    }
    fireEvent({ event_name: EVENT_NAMES.filter, content_type: content_type, ...from })
  }

  const handleClearAll = () => {
    getStaff({ userId: user.id })
    onFilterClear && onFilterClear()
  }

  const onFiltersApply = filterTags => {
    const filteredRegions = filterTags?.regionId
    const filteredStaff = filterTags?.userIds?.split(",")
    // TODO: This needs to be changed
    const regions = regionsData?.find(element => element?.value === filteredRegions)?.name
    const users = filteredStaff?.map(e => staffData?.find(element => element?.value === e)?.title).join(", ")
    fireEvent({
      ...ACTION_TYPES.filter_applied,
      ...(!!filterTags?.userIds && { staff: users }),
      ...(!!filterTags?.regionId && { region_applied: regions }),
      ...from,
    })
  }

  const onClickSaveSearchItem = item => {
    getStaff({
      userId: user?.id,
      regionId: mapQueryStringToFilterObject(decodeURIComponent(item.url)).queryObj?.regionId,
    })
  }

  const dateValue = useMemo(
    () =>
      queryObj.fromDate && queryObj.toDate
        ? [decodeURIComponent(queryObj.fromDate), decodeURIComponent(queryObj.toDate)]
        : canSelectCurrentDate && !excludeCurrentDate
          ? [dayjs().subtract(6, "days").format("YYYY-MM-DD"), dayjs().subtract(0, "days").format("YYYY-MM-DD")]
          : [dayjs().subtract(7, "days").format("YYYY-MM-DD"), dayjs().subtract(1, "days").format("YYYY-MM-DD")],
    [queryObj, canSelectCurrentDate, excludeCurrentDate]
  )

  const dateValueMonthPicker = useMemo(() => queryObj.toDate && decodeURIComponent(queryObj.toDate), [queryObj.toDate])

  const disabledDate = current => {
    const minDate = new Date()
    const maxDate = new Date()
    minDate.setDate(minDate.getDate() - 731)
    maxDate.setDate(canSelectCurrentDate ? maxDate.getDate() : maxDate.getDate() - 1)
    return current && (current < minDate || current > maxDate)
  }

  const disabledDateMonthPicker = date => {
    const currentMonth = dayjs().startOf("month")
    const dateMonth = dayjs(date).startOf("month")
    return dateMonth.isAfter(currentMonth, "month")
  }

  const handleOnChangeDate = useCallback(
    dates => {
      const obj = {
        ...Object.fromEntries([...searchParams]),
        fromDate: encodeURIComponent(dateTimeFunction({ date: dates[0], apiRequest: true, timeZone: true })),
        toDate: encodeURIComponent(
          dateTimeFunction({ date: dates[1], apiRequest: true, format: "YYYY-MM-DDT23:59:59.ssZ" })
        ),
      }
      filtersRef.current?.onSearch(obj, true, undefined, Object.fromEntries([...searchParams]))
      fireEvent({ ...ACTION_TYPES.date_filter_applied, ...from })
    },
    [searchParams]
  )

  const handleOnChangeStaffType = useCallback(
    value => filtersRef.current?.onSearch({ ...queryObj, staffType: value }, true, undefined, queryObj),
    [queryObj]
  )

  const handleOnChangeReportType = useCallback(
    e => {
      filtersRef.current?.onSearch({ ...queryObj, reportType: e.target.value }, true, undefined, queryObj)
    },
    [queryObj]
  )

  const handleOnChangeMonth = useCallback(
    date => {
      const obj = {
        ...Object.fromEntries([...searchParams]),
        toDate: encodeURIComponent(dateTimeFunction({ date: date, apiRequest: true, timeZone: true })),
      }

      filtersRef.current?.onSearch(obj, true, undefined, Object.fromEntries([...searchParams]))
      fireEvent({ ...ACTION_TYPES.date_filter_applied, ...from })
    },
    [searchParams]
  )
  const dt = () => {
    return !!staffData ? staffData : []
  }

  const filterList = useMemo(() => {
    return [
      ...(getPrivilege(privilegeKey, userPrivileges, "all") && !ignoreRegionFilter
        ? [
            {
              label: t("Region"),
              list: regionsData || [],
              type: "select",
              key: "regionId",
              placeholder: t("Select Region"),
              showSearch: false,
              mapFilterObject: ({ userIds, ...rest }) => rest,
              appliedFilter: { searchKey: "regionIds", responseKey: "regions" },
              content_type: CONTENT_TYPES.region_filter,
            },
          ]
        : []),
      {
        label: t("Staff"),
        list: staffData || [],
        type: "treeSelect",
        key: "userIds",
        placeholder: t("Select Staff"),
        treeNodeFilterProp: "title",
        inputLoading: staffLoading,
        appliedFilter: { searchKey: "userIds", responseKey: "users" },
        content_type: CONTENT_TYPES.staff_filter,
      },
    ]
  }, [regionsData, staffData, staffLoading, userPrivileges])

  const renderTopRight = useMemo(() => {
    return (
      <>
        {withReportTypeFilter && (
          <Radio.Group
            style={{ marginInlineEnd: "10px" }}
            buttonStyle="solid"
            value={queryObj?.reportType || "sales"}
            size="medium"
            onChange={handleOnChangeReportType}
          >
            {reportTypes().map(type => (
              <Tooltip key={type.value} title={t(type.tooltip)}>
                <Radio.Button value={type.value}>{t(type.label)}</Radio.Button>
              </Tooltip>
            ))}
          </Radio.Group>
        )}
        {withStaffTypeFilter && (
          <SelectInput
            value={queryObj?.staffType || "aggregated"}
            options={staffTypes()}
            style={{ width: "120px" }}
            onChange={handleOnChangeStaffType}
            defaultValue={staffTypes()?.[0]?.value}
          />
        )}
        {isMonthPicker ? (
          <MonthPicker
            value={dateValueMonthPicker}
            onChange={handleOnChangeMonth}
            disabledDate={disabledDateMonthPicker}
            containerWidth={"18ch"}
          />
        ) : (
          <DateRangePicker
            value={dateValue}
            onChange={handleOnChangeDate}
            disabledDate={disabledDate}
            noOfDaysBefore={0}
          />
        )}
      </>
    )
  }, [isMonthPicker, lastSyncedDate, dateValueMonthPicker, queryObj, searchParams])
  return (
    <>
      {!!title && (
        <Row justify="space-between">
          <Title>{title}</Title>
          {lastSyncedDate && (
            <Group gap="20px">
              <div>
                <Text type="secondary">{t("Last Updated")}</Text>
                <div>
                  <Text type="secondary" className="semiBold">
                    {dateTimeFunction({
                      date: dayjs.utc(lastSyncedDate).local(),
                      timeZone: true,
                      format: TIME_DATE_FORMAT,
                    })}
                  </Text>
                </div>
              </div>
            </Group>
          )}
        </Row>
      )}
      <div className={styles.filtersAffixed}>
        {userPrivileges && (
          <Filters
            list={filterList || []}
            ref={filtersRef}
            searchText=""
            ignoreFilterKeys={["compare", "filteredColumns"]}
            onSaveSearch={() => savedSearchesRef.current?.openSaveSearchModal()}
            saveSearchButtonLabel={t("Create Reports")}
            onFilterValueChange={onFilterValueChange}
            onFiltersApply={onFiltersApply}
            onFilterClear={handleClearAll}
            haveDependentRequests
            isFetching={isFetching}
            renderTopRight={() => renderTopRight}
          />
        )}
      </div>

      <SavedSearches
        className="mbe-12"
        moduleObject={searchModule}
        ref={savedSearchesRef}
        applySavedFilter={filterObj => {
          filtersRef.current?.onSearch(filterObj, true, undefined, { ...queryObj })
          fireEvent({ ...ACTION_TYPES.save_search_used, ...from })
        }}
        onSaveSearch={e => {
          fireEvent({ ...ACTION_TYPES.save_search, response: e.status, ...from })
        }}
        removeDateKeysBeforeSave
        onClickSearch={onClickSaveSearchItem}
        ignoreKeys={isMonthPicker ? ["toDate"] : ["fromDate", "toDate"]}
        label={t("My Reports")}
      />
    </>
  )
}
